/**
 * Where to send someone after they sign in.
 *
 * The destination arrives in a query string (`/login?redirect=/jobs/119`), which
 * means it is attacker-supplied text, not a trusted path. Only same-site absolute
 * paths are honoured; anything else falls back to the caller's default.
 *
 * Rejected, and why:
 *   https://evil.example      an absolute URL to somewhere else
 *   //evil.example            protocol-relative, which browsers treat as absolute
 *   javascript:alert(1)       a scheme, not a path
 *   jobs/119                  relative, so it would resolve against whatever page
 *                             happens to be current — not what the sender meant
 *   /login, /register         would bounce straight back here, looping
 */
const AUTH_PAGES = ['/login', '/register', '/forgot-password', '/employer/recruiter-login'];

/*
 * True if the value holds a control character or a space.
 *
 * Both are how a crafted value slips past the leading-slash check below: some
 * browsers strip a tab out of "/<tab>/evil.example" and treat what is left as a
 * host. Written as a character-code scan rather than a regex so the range is
 * unambiguous — printable ASCII, hyphens and dots included, is left alone.
 */
const hasControlOrSpace = (value) => {
    for (let i = 0; i < value.length; i += 1) {
        const code = value.charCodeAt(i);
        if (code <= 0x20 || code === 0x7f) return true;
    }
    return false;
};

export const safeRedirectPath = (value) => {
    if (typeof value !== 'string') return null;
    const path = value.trim();

    // Must be a single-slash absolute path. `//host` is protocol-relative and
    // leaves the site, and `/\host` does the same in some browsers.
    if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return null;
    if (hasControlOrSpace(path)) return null;

    // Sending someone back to a sign-in page after signing in is a loop.
    const base = path.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
    if (AUTH_PAGES.includes(base.toLowerCase())) return null;

    return path;
};

export default safeRedirectPath;
